import { parseTaskId } from '../../screens/TaskBoard/taskUtils';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { client } from '../../networking/client';
import type { RootState } from '../store';

// DTOs and query parameters from the Tasks Manager OpenAPI definition.
export const TASK_PRIORITIES = ['low', 'medium', 'high'] as const;
export type TaskPriority = typeof TASK_PRIORITIES[number];

export interface Task {
  task_id?: number;
  type?: string;
  title?: string;
  status?: string;
  priority?: TaskPriority;
  description?: string;
  merchant_id?: number;
  customer_id?: number;
  // The API returns null; task components resolve names from the customer list.
  customer_name?: string | null;
  record_state?: string;
  person_id_create?: number;
  person_id_assign?: number;
  person_name_assign?: string;
  person_name_create?: string;
  datetime_create?: string;
  datetime_planned?: string;
  datetime_finish?: string;
  datetime_start?: string;
}

export interface TaskHistory {
  history_id?: number;
  task_id?: number;
  history_type?: string;
  log_description?: string;
  person_id?: number;
  person_name?: string;
  datetime?: string;
}

export interface TaskAttachmentMetadata {
  attachment_id: number;
  task_id: number;
  name: string;
  type: string;
  size: number;
  datetime_create: string;
  person_id_create: number;
}

export interface TaskAttachment extends TaskAttachmentMetadata {
  body?: string;
  s3_path?: string;
}

export interface FetchTasksParams {
  status?: string;
  type?: string;
  priority?: TaskPriority;
  dateFrom?: string;
  dateTo?: string;
}

export interface TaskCustomerData {
  externalId: number;
  roles: string[];
  emailAddress: string;
  language: string;
  name: string;
  givenName: string;
}

export interface TaskMerchantPersonData {
  id: number;
  externalId: number;
  roles: string[];
  language?: string;
  givenName?: string;
  name?: string;
  emailAddress?: string;
}

export interface CreateTaskParams {
  title: string;
  priority?: TaskPriority;
  description?: string;
  status?: 'open' | 'in progress' | 'closed' | 'discarded';
  // This spelling is part of the backend request schema.
  datetimePlaned?: string;
  datetimeFinish?: string;
  datetimeStart?: string;
  customer?: TaskCustomerData;
  merchantPersonAssign?: TaskMerchantPersonData;
}

export const TASK_STATUSES = ['open', 'in progress', 'closed', 'discarded'] as const;
export type TaskStatus = typeof TASK_STATUSES[number];

interface TaskUpdateFields {
  title: string;
  description: string;
  status: TaskStatus;
  priority: TaskPriority;
  datetimePlaned: string;
  datetimeFinish: string;
  datetimeStart: string;
  customer: TaskCustomerData;
}

// The service stores one history change per task:update event.
export type TaskUpdatePayloadRequest = { taskId: number } & {
  [K in keyof TaskUpdateFields]: Pick<TaskUpdateFields, K> &
    Partial<Record<Exclude<keyof TaskUpdateFields, K>, never>>;
}[keyof TaskUpdateFields];
export interface TaskAssignPayloadRequest {
  taskId: number;
  person: TaskMerchantPersonData;
}

export interface CommentCreatePayload {
  taskId: number;
  text: string;
}

export interface UploadTaskAttachmentParams {
  taskId: number;
  file: File;
}

type RequestStatus = 'idle' | 'pending' | 'fulfilled' | 'rejected';

export interface TasksState {
  requestIds: Partial<
    Record<'tasks' | 'task' | 'history' | 'attachments' | 'attachment' | 'upload', string>
  >;
  optimisticStatuses: Record<
    number,
    {
      requestId: string;
      status: TaskStatus;
      previousStatus?: string;
    }
  >;
  tasks: Task[];
  task: Task | null;
  taskHistory: TaskHistory[];
  attachments: TaskAttachment[];
  attachment: TaskAttachment | null;
  uploadedAttachment: TaskAttachmentMetadata | null;
  status: RequestStatus;
  taskStatus: RequestStatus;
  historyStatus: RequestStatus;
  attachmentsStatus: RequestStatus;
  attachmentStatus: RequestStatus;
  uploadingStatus: RequestStatus;
}

const initialState: TasksState = {
  requestIds: {},
  optimisticStatuses: {},
  tasks: [],
  task: null,
  taskHistory: [],
  attachments: [],
  attachment: null,
  uploadedAttachment: null,
  status: 'idle',
  taskStatus: 'idle',
  historyStatus: 'idle',
  attachmentsStatus: 'idle',
  attachmentStatus: 'idle',
  uploadingStatus: 'idle',
};

const taskApi = '/task/api';
const normalizeTask = (task: Task): Task => ({ ...task, task_id: parseTaskId(task.task_id) });
export const MAX_TASK_ATTACHMENT_SIZE = 5 * 1024 * 1024;

export const fetchTasks = createAsyncThunk(
  'tasks/fetchAll',
  async (params: FetchTasksParams | undefined, { rejectWithValue }) => {
    try {
      const response = await client.get<Task[]>(`${taskApi}/tasks`, { params });
      return response.data.map(normalizeTask);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const createTask = createAsyncThunk(
  'tasks/create',
  async (payload: CreateTaskParams, { rejectWithValue }) => {
    try {
      const response = await client.post<Task>(`${taskApi}/task`, payload);
      return normalizeTask(response.data);
    } catch (err: any) {
      return rejectWithValue(errorMessage(err?.response?.data));
    }
  }
);

export const fetchTaskById = createAsyncThunk(
  'tasks/fetchById',
  async (taskId: number, { rejectWithValue }) => {
    try {
      return await readTaskSnapshot(taskId);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

// Read without replacing the selected task in Redux during socket confirmation.
export const readTaskSnapshot = async (taskId: number, signal?: AbortSignal): Promise<Task> => {
  const response = await client.get<Task>(`${taskApi}/task/${taskId}`, { signal });
  return normalizeTask(response.data);
};

export const readTaskHistorySnapshot = async (
  taskId: number,
  signal?: AbortSignal
): Promise<TaskHistory[]> => {
  const response = await client.get<TaskHistory[]>(`${taskApi}/task-history/${taskId}`, { signal });
  return response.data;
};

export const fetchTaskHistory = createAsyncThunk(
  'tasks/fetchHistory',
  async (taskId: number, { rejectWithValue }) => {
    try {
      return await readTaskHistorySnapshot(taskId);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const fetchTaskAttachments = createAsyncThunk(
  'tasks/fetchAttachments',
  async (taskId: number, { rejectWithValue }) => {
    try {
      const response = await client.get<TaskAttachment[]>(`${taskApi}/attachments/${taskId}/task`);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const fetchTaskAttachmentById = createAsyncThunk(
  'tasks/fetchAttachmentById',
  async (attachmentId: number, { rejectWithValue }) => {
    try {
      const response = await client.get<TaskAttachment>(`${taskApi}/attachment/${attachmentId}`);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const uploadTaskAttachment = createAsyncThunk(
  'tasks/uploadAttachment',
  async ({ taskId, file }: UploadTaskAttachmentParams, { rejectWithValue }) => {
    if (file.size > MAX_TASK_ATTACHMENT_SIZE) {
      return rejectWithValue({ message: 'Attachment must not exceed 5 MB.' });
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('taskId', String(taskId));
      // The browser sets the multipart boundary for FormData.
      const response = await client.post<TaskAttachmentMetadata>(`${taskApi}/attachment`, formData);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

const errorMessage = (payload: any): string => {
  const message = payload?.message ?? payload?.error ?? 'Something went wrong.';
  return Array.isArray(message) ? message.join(', ') : String(message);
};

const showError = (payload: any) => {
  toast.error(errorMessage(payload), {
    position: toast.POSITION.BOTTOM_RIGHT,
  });
};

export const tasksSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    resetTasks: () => initialState,
    startOptimisticTaskStatus: (
      state,
      action: PayloadAction<{
        taskId: number;
        status: TaskStatus;
        requestId: string;
      }>
    ) => {
      const { taskId, status, requestId } = action.payload;
      const task = state.tasks.find((item) => item.task_id === taskId);
      if (!task || state.optimisticStatuses[taskId]) return;
      state.optimisticStatuses[taskId] = { requestId, status, previousStatus: task.status };
      task.status = status;
      if (state.task?.task_id === taskId) state.task.status = status;
    },
    confirmOptimisticTaskStatus: (
      state,
      action: PayloadAction<{
        task: Task;
        requestId: string;
      }>
    ) => {
      const { task, requestId } = action.payload;
      const taskId = task.task_id;
      if (taskId == null || state.optimisticStatuses[taskId]?.requestId !== requestId) return;
      const index = state.tasks.findIndex((item) => item.task_id === taskId);
      if (index >= 0) state.tasks[index] = { ...state.tasks[index], ...task };
      if (state.task?.task_id === taskId) state.task = { ...state.task, ...task };
      delete state.optimisticStatuses[taskId];
      // Discard list responses started before confirmation. The socket action
      // starts a fresh refresh immediately after this reducer runs.
      delete state.requestIds.tasks;
      state.status = 'fulfilled';
    },
    rollbackOptimisticTaskStatus: (
      state,
      action: PayloadAction<{
        taskId: number;
        requestId: string;
      }>
    ) => {
      const { taskId, requestId } = action.payload;
      const optimistic = state.optimisticStatuses[taskId];
      if (!optimistic || optimistic.requestId !== requestId) return;
      const task = state.tasks.find((item) => item.task_id === taskId);
      if (task) task.status = optimistic.previousStatus;
      if (state.task?.task_id === taskId) state.task.status = optimistic.previousStatus;
      delete state.optimisticStatuses[taskId];
      delete state.requestIds.tasks;
      state.status = 'fulfilled';
    },
    resetTaskDetails: (state) => {
      state.requestIds = { tasks: state.requestIds.tasks };
      state.task = null;
      state.taskHistory = [];
      state.attachments = [];
      state.attachment = null;
      state.uploadedAttachment = null;
      state.taskStatus = 'idle';
      state.historyStatus = 'idle';
      state.attachmentsStatus = 'idle';
      state.attachmentStatus = 'idle';
      state.uploadingStatus = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTasks.pending, (state, action) => {
        state.requestIds.tasks = action.meta.requestId;
        state.status = 'pending';
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        if (state.requestIds.tasks !== action.meta.requestId) return;
        state.tasks = action.payload.map((task) => {
          const optimistic =
            task.task_id != null ? state.optimisticStatuses[task.task_id] : undefined;
          if (!optimistic) return task;
          // Keep the optimistic position, but retain the latest server status
          // to restore if this operation is rejected.
          optimistic.previousStatus = task.status;
          return { ...task, status: optimistic.status };
        });
        state.status = 'fulfilled';
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        if (state.requestIds.tasks !== action.meta.requestId) return;
        state.status = 'rejected';
        if (!action.meta.aborted) showError(action.payload);
      })
      .addCase(fetchTaskById.pending, (state, action) => {
        state.requestIds.task = action.meta.requestId;
        state.task = null;
        state.taskStatus = 'pending';
      })
      .addCase(fetchTaskById.fulfilled, (state, action) => {
        if (state.requestIds.task !== action.meta.requestId) return;
        const optimistic =
          action.payload.task_id != null
            ? state.optimisticStatuses[action.payload.task_id]
            : undefined;
        state.task = optimistic ? { ...action.payload, status: optimistic.status } : action.payload;
        state.taskStatus = 'fulfilled';
      })
      .addCase(fetchTaskById.rejected, (state, action) => {
        if (state.requestIds.task !== action.meta.requestId) return;
        state.taskStatus = 'rejected';
        if (!action.meta.aborted) showError(action.payload);
      })
      .addCase(fetchTaskHistory.pending, (state, action) => {
        state.requestIds.history = action.meta.requestId;
        state.taskHistory = [];
        state.historyStatus = 'pending';
      })
      .addCase(fetchTaskHistory.fulfilled, (state, action) => {
        if (state.requestIds.history !== action.meta.requestId) return;
        state.taskHistory = action.payload;
        state.historyStatus = 'fulfilled';
      })
      .addCase(fetchTaskHistory.rejected, (state, action) => {
        if (state.requestIds.history !== action.meta.requestId) return;
        state.historyStatus = 'rejected';
        if (!action.meta.aborted) showError(action.payload);
      })
      .addCase(fetchTaskAttachments.pending, (state, action) => {
        state.requestIds.attachments = action.meta.requestId;
        state.attachments = [];
        state.attachmentsStatus = 'pending';
      })
      .addCase(fetchTaskAttachments.fulfilled, (state, action) => {
        if (state.requestIds.attachments !== action.meta.requestId) return;
        state.attachments = action.payload;
        state.attachmentsStatus = 'fulfilled';
      })
      .addCase(fetchTaskAttachments.rejected, (state, action) => {
        if (state.requestIds.attachments !== action.meta.requestId) return;
        state.attachmentsStatus = 'rejected';
        if (!action.meta.aborted) showError(action.payload);
      })
      .addCase(fetchTaskAttachmentById.pending, (state, action) => {
        state.requestIds.attachment = action.meta.requestId;
        state.attachment = null;
        state.attachmentStatus = 'pending';
      })
      .addCase(fetchTaskAttachmentById.fulfilled, (state, action) => {
        if (state.requestIds.attachment !== action.meta.requestId) return;
        state.attachment = action.payload;
        state.attachmentStatus = 'fulfilled';
      })
      .addCase(fetchTaskAttachmentById.rejected, (state, action) => {
        if (state.requestIds.attachment !== action.meta.requestId) return;
        state.attachmentStatus = 'rejected';
        if (!action.meta.aborted) showError(action.payload);
      })
      .addCase(uploadTaskAttachment.pending, (state, action) => {
        state.requestIds.upload = action.meta.requestId;
        state.uploadedAttachment = null;
        state.uploadingStatus = 'pending';
      })
      .addCase(uploadTaskAttachment.fulfilled, (state, action) => {
        if (state.requestIds.upload !== action.meta.requestId) return;
        state.uploadedAttachment = action.payload;
        state.uploadingStatus = 'fulfilled';
        toast.success('Successfully uploaded the attachment.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      })
      .addCase(uploadTaskAttachment.rejected, (state, action) => {
        if (state.requestIds.upload !== action.meta.requestId) return;
        state.uploadingStatus = 'rejected';
        if (!action.meta.aborted) showError(action.payload);
      });
  },
});

export const {
  resetTasks,
  resetTaskDetails,
  startOptimisticTaskStatus,
  confirmOptimisticTaskStatus,
  rollbackOptimisticTaskStatus,
} = tasksSlice.actions;
export default tasksSlice.reducer;

export const selectTasks = (state: RootState) => state.tasksReducer.tasks;
export const selectTask = (state: RootState) => state.tasksReducer.task;
export const selectTaskHistory = (state: RootState) => state.tasksReducer.taskHistory;
export const selectTaskAttachments = (state: RootState) => state.tasksReducer.attachments;
export const selectTaskAttachment = (state: RootState) => state.tasksReducer.attachment;
export const selectUploadedTaskAttachment = (state: RootState) =>
  state.tasksReducer.uploadedAttachment;
export const selectTasksStatus = (state: RootState) => state.tasksReducer.status;
export const selectTaskStatus = (state: RootState) => state.tasksReducer.taskStatus;
export const selectTaskHistoryStatus = (state: RootState) => state.tasksReducer.historyStatus;
export const selectTaskAttachmentsStatus = (state: RootState) =>
  state.tasksReducer.attachmentsStatus;
export const selectTaskAttachmentStatus = (state: RootState) => state.tasksReducer.attachmentStatus;
export const selectTaskUploadingStatus = (state: RootState) => state.tasksReducer.uploadingStatus;
